import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CloudUpload as UploadIcon, Send as SendIcon } from '@mui/icons-material';
import PublicSiteHeader from '@components/layout/PublicSiteHeader';
import SiteFooter from '@components/layout/SiteFooter';
import { brandColors } from '@config/brandTokens';
import { CAREER_ROLES, getCareerRole, type CareerRoleId } from '@/content/careersRoles';

const CareersPage: React.FC = () => {
  const [roleId, setRoleId] = useState<CareerRoleId>(CAREER_ROLES[0].id);
  const role = useMemo(() => getCareerRole(roleId) || CAREER_ROLES[0], [roleId]);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [portfolioUrl, setPortfolioUrl] = useState('');
  const [yearsExperience, setYearsExperience] = useState('');
  const [coverLetter, setCoverLetter] = useState('');
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!cvFile) {
      setError('Please attach your CV / resume.');
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('roleId', role.id);
      form.append('roleTitle', role.title);
      form.append('fullName', fullName.trim());
      form.append('email', email.trim());
      form.append('phone', phone.trim());
      form.append('city', city.trim());
      form.append('country', country.trim());
      form.append('linkedinUrl', linkedinUrl.trim());
      form.append('portfolioUrl', portfolioUrl.trim());
      form.append('yearsExperience', yearsExperience.trim());
      form.append('coverLetter', coverLetter.trim());
      form.append('cv', cvFile);

      const res = await fetch('/api/careers/applications', {
        method: 'POST',
        body: form,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to submit application');
      }
      setSuccess(data.message || 'Application submitted.');
      setFullName('');
      setEmail('');
      setPhone('');
      setCity('');
      setCountry('');
      setLinkedinUrl('');
      setPortfolioUrl('');
      setYearsExperience('');
      setCoverLetter('');
      setCvFile(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: brandColors.ice, display: 'flex', flexDirection: 'column' }}>
      <PublicSiteHeader />
      <Box sx={{ flex: 1, px: { xs: 1.5, md: 3 }, py: { xs: 2.5, md: 3 }, maxWidth: 1200, width: '100%', mx: 'auto' }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: '"Sora", Arial, sans-serif',
            fontWeight: 800,
            fontSize: { xs: '1.55rem', md: '1.85rem' },
            color: brandColors.navy,
            mb: 0.5,
          }}
        >
          Careers
        </Typography>
        <Typography sx={{ color: brandColors.slate, mb: 2.5, maxWidth: 720, lineHeight: 1.55 }}>
          Join EncycGames — build the first unified sports platform with structured, age-wise learning.
          Select a role, then submit your details and CV.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                bgcolor: brandColors.white,
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                overflow: 'hidden',
                position: { md: 'sticky' },
                top: { md: 80 },
              }}
            >
              <Box sx={{ px: 2, py: 1.25, bgcolor: brandColors.navy, color: brandColors.white }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>Open roles</Typography>
              </Box>
              <List disablePadding>
                {CAREER_ROLES.map((item) => {
                  const selected = item.id === roleId;
                  return (
                    <ListItemButton
                      key={item.id}
                      selected={selected}
                      onClick={() => setRoleId(item.id)}
                      sx={{
                        borderBottom: '1px solid #f1f5f9',
                        alignItems: 'flex-start',
                        '&.Mui-selected': { bgcolor: '#eff6ff' },
                      }}
                    >
                      <ListItemText
                        primary={item.title}
                        secondary={`${item.team} · ${item.type}`}
                        primaryTypographyProps={{
                          fontWeight: selected ? 800 : 600,
                          fontSize: '0.88rem',
                          color: brandColors.navy,
                        }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box
              sx={{
                bgcolor: brandColors.white,
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                p: { xs: 2, md: 3 },
                mb: 2,
              }}
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Chip size="small" label={role.team} sx={{ fontWeight: 700 }} />
                <Chip size="small" label={role.type} sx={{ fontWeight: 700 }} />
                <Chip size="small" label={role.location} variant="outlined" sx={{ fontWeight: 600 }} />
              </Stack>
              <Typography
                sx={{
                  fontFamily: '"Sora", Arial, sans-serif',
                  fontWeight: 800,
                  fontSize: '1.35rem',
                  color: brandColors.navy,
                  mb: 1,
                }}
              >
                {role.title}
              </Typography>
              <Typography sx={{ color: brandColors.slate, mb: 2, lineHeight: 1.55 }}>{role.summary}</Typography>

              <Typography sx={{ fontWeight: 800, color: brandColors.navy, mb: 0.75 }}>Responsibilities</Typography>
              <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2.5 }}>
                {role.responsibilities.map((line) => (
                  <Typography component="li" key={line} sx={{ mb: 0.5, color: '#334155', fontSize: '0.95rem' }}>
                    {line}
                  </Typography>
                ))}
              </Box>
              <Typography sx={{ fontWeight: 800, color: brandColors.navy, mb: 0.75 }}>Requirements</Typography>
              <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.5 }}>
                {role.requirements.map((line) => (
                  <Typography component="li" key={line} sx={{ mb: 0.5, color: '#334155', fontSize: '0.95rem' }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Box>

            <Box
              component="form"
              onSubmit={onSubmit}
              sx={{
                bgcolor: brandColors.white,
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                p: { xs: 2, md: 3 },
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Sora", Arial, sans-serif',
                  fontWeight: 800,
                  color: brandColors.navy,
                  mb: 0.5,
                }}
              >
                Apply — {role.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Personal details and CV (PDF/DOC/DOCX, max 8 MB).
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                  {success}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Years of experience"
                    value={yearsExperience}
                    onChange={(e) => setYearsExperience(e.target.value)}
                    placeholder="e.g. 3–5"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="City" value={city} onChange={(e) => setCity(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="LinkedIn URL"
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Portfolio / website"
                    value={portfolioUrl}
                    onChange={(e) => setPortfolioUrl(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Cover note"
                    value={coverLetter}
                    onChange={(e) => setCoverLetter(e.target.value)}
                    placeholder="Why EncycGames? Relevant sports or product experience?"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    sx={{ textTransform: 'none', fontWeight: 700, borderColor: brandColors.navy, color: brandColors.navy }}
                  >
                    {cvFile ? cvFile.name : 'Upload CV / resume *'}
                    <input
                      hidden
                      type="file"
                      accept=".pdf,.doc,.docx,.txt,.rtf,application/pdf"
                      onChange={(e) => setCvFile(e.target.files?.[0] || null)}
                    />
                  </Button>
                </Grid>
              </Grid>

              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={<SendIcon />}
                sx={{
                  mt: 2.5,
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: brandColors.orange,
                  '&:hover': { bgcolor: '#f07a3d' },
                }}
              >
                {submitting ? 'Submitting…' : 'Submit application'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <SiteFooter />
    </Box>
  );
};

export default CareersPage;
